import type { InfiniteData, UseSuspenseInfiniteQueryOptions, } from '@tanstack/react-query';
import { useSuspenseInfiniteQuery, useSuspenseQuery, } from '@tanstack/react-query';
import type { FacebookPage } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import { apiClient } from 'lib/api';
import type { ListResponse, SortingParams } from 'types/shared';

interface GetFacebookPagesParams extends Partial<SortingParams> {
  name?: string;
  limit?: number;
  offset?: number;
}

export const getFacebookPages = async (
  params?: GetFacebookPagesParams,
  signal?: AbortSignal
): Promise<ListResponse<FacebookPage>> => {
  const res = await apiClient.get<ListResponse<FacebookPage>>(
    queryKeys.facebookPages,
    {
      signal,
      params,
    }
  );
  return res.data;
};

interface UseFacebookPagesOptions<TData> {
  config?: QueryConfig<typeof getFacebookPages, TData>;
  params?: GetFacebookPagesParams;
}

export const useFacebookPages = <TData = ListResponse<FacebookPage>>({
  config,
  params,
}: UseFacebookPagesOptions<TData> = {}) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getFacebookPages(params, signal),
    queryKey: [queryKeys.facebookPages, params],
  });
};

interface UseInfiniteFacebookPagesOptions {
  config?: Omit<
    UseSuspenseInfiniteQueryOptions<
      ListResponse<FacebookPage>,
      unknown,
      InfiniteData<ListResponse<FacebookPage>>,
      readonly unknown[],
      number
    >,
    'queryFn' | 'queryKey' | 'initialPageParam' | 'getNextPageParam'
  >;
  params?: GetFacebookPagesParams;
}

export const useInfiniteFacebookPages = ({
  config,
  params,
}: UseInfiniteFacebookPagesOptions = {}) => {
  return useSuspenseInfiniteQuery({
    ...config,
    queryFn: ({ pageParam = 1, signal }) =>
      getFacebookPages(
        {
          ...params,
          offset: pageParam,
          limit: params?.limit ?? 100,
        },
        signal
      ),
    queryKey: [
      queryKeys.facebookPages,
      'infinite',
      ...Object.values(params ?? {}),
    ],
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.count;
      const loadedItems = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0
      );

      if (loadedItems >= totalItems) {
        return undefined;
      }

      return allPages.length + 1;
    },
    initialPageParam: 1,
  });
};
